import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NumberFormatCustom } from '~/components/Chat/AccessoriesValue';
import Input from '~/components/Input';
import InputSelect from '~/components/InputSelect';
import IOSSwitch from '~/components/IOSSwitch';
import SidebarMesages from '~/components/PersonalCoverage/SidebarMesages';
import { useContractLoader } from '~/hooks/useContractLoader';
import ModalHelperSerieNumber from '~/modals/ModalHelpSerieNumber';
import { ContractPostEquipment, Manufacturer } from '~/services/modules/contract/types';
import useStore, { storeState } from '~/store';
import { getErrorMessage, logErrorWithContext } from '~/utils/errorHandler';
import searchNameEquipment from '~/utils/searchNameEquipment';
import * as styles from './styles';
import schema from './validation';

const InternationalCoverageItems = [
  {
    value: '1',
    label: 'Não contratada'
  },
  {
    value: '2',
    label: 'América do Sul e América Central'
  },
  {
    value: '3',
    label: 'América do Norte e Europa'
  },
  {
    value: '4',
    label: 'África, Ásia, Oceania e Antártida'
  },
  {
    value: '5',
    label: 'Qualquer Continente'
  }
];

const MaterialsItems = [
  {
    value: '1',
    label: 'Aço ou alumínio'
  },
  {
    value: '2',
    label: 'Carbono'
  }
];

export default function HelpZipcodePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  // Hook para carregar dados do contrato
  const { contract_id } = useContractLoader({ stepName: 'step3' });

  const { manufacturers, contractAsyncGetManufacturer, contractAsyncPostEquipment, quote, insured_item } = useStore(
    (store) => ({
      contractAsyncGetManufacturer: store.contractAsyncGetManufacturer,
      contractAsyncPostEquipment: store.contractAsyncPutEquipment,
      manufacturers: store.manufacturers,
      quote: store.quote,
      insured_item: store.insured_item
    })
  );

  const [defaultValues, setDefaultValues] = useState({
    typeEquipment: '',
    value: 0,
    accessoriesValue: 0,
    isNewEquipment: false,
    hasInvoice: false,
    yearOfManufacture: '',
    equipmentMaterial: '',
    internationalCoverage: '',
    participatesCompetitions: false
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, dirtyFields }
  } = useForm<ContractPostEquipment>({
    resolver: yupResolver(schema)
  });
  const formatItemSelect = (items: Array<Manufacturer>) => {
    const newItems = items?.map((item: Manufacturer) => ({
      value: item.id,
      label: item.name
    }));
    return newItems;
  };
  const formatItemYears = () => {
    const year = new Date().getFullYear();
    const newItems = [];
    for (let index = year; index > year - 10; index--) {
      newItems.push({
        value: index.toString(),
        label: index.toString()
      });
    }
    return newItems;
  };

  const onSubmit: SubmitHandler<ContractPostEquipment> = async (data) => {
    if (contract_id) {
      setLoading(true);
      try {
        const orgParam = searchParams.get('org');
        const useInsuredItem =
          insured_item && (insured_item.year_of_manufacture != null || insured_item.is_new != null);
        await contractAsyncPostEquipment(
          contract_id,
          {
            model: data.model,
            manufacturer_id: String(data.manufacturer_id),
            serial_number: typeof data.serial_number === 'string' ? data.serial_number.trim() : data.serial_number,
            year_of_manufacture: useInsuredItem ? insured_item.year_of_manufacture : quote?.yearOfManufacture,
            is_new: useInsuredItem ? insured_item.is_new ?? false : quote?.isNewEquipment ?? false
          },
          orgParam ?? undefined
        );
        setLoading(false);

        const contract_hash_id = searchParams.get('cid');
        const source = searchParams.get('source');
        const params = new URLSearchParams();
        if (contract_hash_id) params.set('cid', contract_hash_id);
        if (source) params.set('source', source);
        if (orgParam) params.set('org', orgParam);

        navigate(`/contract/file?${params.toString()}`);
      } catch (error: any) {
        setLoading(false);

        logErrorWithContext(error, 'contract_step3_equipment', {
          contractId: contract_id,
          manufacturerId: data.manufacturer_id,
          hasSerialNumber: !!data.serial_number
        });

        const errorResponse = getErrorMessage(error);
        storeState.addToast(errorResponse);
      }
    } else {
      storeState.addToast({
        title: 'Não foi possível recuperar o contrato.',
        message: '',
        type: 'warning'
      });
    }
  };

  useEffect(() => {
    contractAsyncGetManufacturer();
  }, []);

  // Preencher com dados do contrato (insured_item) quando existirem, ou fallback na quote
  useEffect(() => {
    const hasInsuredItem =
      insured_item &&
      (insured_item.equipment_type != null ||
        insured_item.insured_amount != null ||
        insured_item.equipment_type_id != null);
    if (hasInsuredItem) {
      setDefaultValues({
        typeEquipment: insured_item.equipment_type || insured_item.type_insured_item || '',
        value: insured_item.insured_amount ?? 0,
        accessoriesValue: insured_item.total_value_accessories ?? 0,
        isNewEquipment: insured_item?.is_new ?? false,
        hasInvoice: insured_item?.has_invoice ?? false,
        yearOfManufacture: String(insured_item.year_of_manufacture ?? ''),
        equipmentMaterial: String(insured_item.equipment_material_type ?? ''),
        internationalCoverage: String(insured_item.international_coverage ?? ''),
        participatesCompetitions: insured_item?.participates_competition ?? false
      });
      return;
    }
    if (quote?.equipment != null || quote?.equipmentValue != null) {
      setDefaultValues({
        typeEquipment: searchNameEquipment(quote.equipment),
        value: quote.equipmentValue ?? 0,
        accessoriesValue: quote.accessoriesValue ?? 0,
        isNewEquipment: quote.isNewEquipment ?? false,
        hasInvoice: quote.hasInvoice ?? false,
        yearOfManufacture: String(quote.yearOfManufacture ?? ''),
        equipmentMaterial: String(quote.equipmentMaterial ?? ''),
        internationalCoverage: String(quote.continents ?? ''),
        participatesCompetitions: quote.participatesInCompetitions ?? false
      });
    }
  }, [insured_item, quote]);

  return (
    <Container maxWidth="lg" sx={styles.container}>
      <Grid container spacing={2}>
        <Grid item md={8} xs={12}>
          <Card>
            <CardContent>
              <Box sx={styles.headerContainer}>
                <Typography variant="h6" align="center">
                  Dados para contrato
                </Typography>
              </Box>
              <Divider variant="middle" />
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={styles.containerForm}>
                  <Typography sx={{ fontSize: { xs: 16, sm: 20 } }} variant="h6" fontWeight="bold">
                    3 - Detalhes do Equipamento
                  </Typography>
                  <Input
                    name="typeEquipment"
                    label="Tipo de Equipamento"
                    placeholder="Patinete Elétrico"
                    value={defaultValues.typeEquipment}
                    disabled
                  />
                  <Input
                    name="value"
                    label="Valor"
                    placeholder="R$ 3.000,00"
                    value={defaultValues.value}
                    startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                    inputComponent={NumberFormatCustom as any}
                    disabled
                  />
                  <Input
                    name="accessoriesValue"
                    label="Valor total do(s) acessório(s)"
                    value={defaultValues.accessoriesValue}
                    startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                    inputComponent={NumberFormatCustom as any}
                    disabled
                  />
                  <FormGroup sx={styles.switchStyle}>
                    <FormControlLabel
                      control={<IOSSwitch sx={{ m: 1 }} onChange={() => {}} />}
                      checked={defaultValues?.isNewEquipment ?? false}
                      disabled
                      label="É um equipamento novo (máximo 90 dias da data da compra)."
                    />
                  </FormGroup>
                  <FormGroup sx={styles.switchStyle}>
                    <FormControlLabel
                      control={<IOSSwitch sx={{ m: 1 }} onChange={() => {}} />}
                      checked={defaultValues?.hasInvoice ?? false}
                      disabled
                      label="Eu tenho a nota fiscal."
                    />
                  </FormGroup>
                  <InputSelect
                    name="yearOfManufacture"
                    label="Ano de Fabricação"
                    items={formatItemYears()}
                    setValue={() => {}}
                    defaultValue={defaultValues.yearOfManufacture}
                    disabled
                  />
                  <InputSelect
                    name="brand"
                    label="Marca"
                    placeholder="Escolha a marca"
                    items={formatItemSelect(manufacturers)}
                    setValue={(value) => {
                      setValue('manufacturer_id', value as string);
                    }}
                    error={errors?.manufacturer_id ? true : false}
                    helperText={errors?.manufacturer_id?.message}
                  />
                  <Controller
                    name="model"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="Modelo"
                          placeholder="MR255"
                          error={errors?.model ? true : false}
                          isDirty={dirtyFields.model}
                          helperText={errors?.model?.message}
                          {...field}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="serial_number"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="Chassi do equipamento"
                          placeholder="XXXXXXXXXX"
                          error={errors?.serial_number ? true : false}
                          isDirty={dirtyFields.serial_number}
                          helperText={errors?.serial_number?.message}
                          IconHelper={ModalHelperSerieNumber}
                          {...field}
                        />
                      </>
                    )}
                  />
                  <InputSelect
                    name="equipmentMaterial"
                    label="Material do Quadro"
                    items={MaterialsItems}
                    setValue={() => {}}
                    defaultValue={defaultValues.equipmentMaterial}
                    disabled
                  />
                  <InputSelect
                    name="internationalCoverage"
                    label="Cobertura Internacional"
                    items={InternationalCoverageItems}
                    setValue={() => {}}
                    defaultValue={defaultValues.internationalCoverage}
                    disabled
                  />
                  <FormGroup sx={styles.switchStyle}>
                    <FormControlLabel
                      control={<IOSSwitch sx={{ m: 1 }} onChange={() => {}} />}
                      checked={defaultValues.participatesCompetitions}
                      disabled
                      label="Participa de competição?"
                    />
                  </FormGroup>
                </Box>
                <Box sx={styles.containerButton}>
                  <LoadingButton sx={{ fontSize: { xs: 16, sm: 20 } }} loading={loading} type="submit">
                    salvar e sair
                  </LoadingButton>
                  <LoadingButton
                    data-testid="contract-step3-next"
                    sx={{ fontSize: { xs: 16, sm: 20 } }}
                    loading={loading}
                    variant="contained"
                    type="submit"
                  >
                    Próximo passo
                  </LoadingButton>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          md={4}
          xs={12}
          sx={{
            display: {
              xs: 'none',
              lg: 'block'
            }
          }}
        >
          <Card>
            <CardContent>
              <SidebarMesages showProduct={true} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
